import { useEffect, useMemo, useRef, useState } from "react";
import type { TextPart } from "../../../../models/ManualAuthoring";
import { useManualAuthoringStore } from "../../../../store/manualAuthoringStore";
import "./structuredInlineTextEditor.css";

interface Props {
  textParts: TextPart[];
  onChange: (newParts: TextPart[]) => void;
  onDone: () => void;
}

type RefKind = "section" | "subsection" | "figure" | "table";

type RefTarget = {
  targetId: string;
  label: string;
  kind: RefKind;
};

type RefTreeSubSection = {
  id: string;
  title: string;
  refs: RefTarget[];
};

type RefTreeSection = {
  id: string;
  title: string;
  refs: RefTarget[];
  subSections: RefTreeSubSection[];
};

export default function StructuredInlineTextEditor({
  textParts,
  onChange,
  onDone,
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const manual = useManualAuthoringStore((s) => s.presentManual);

  const [showRefPicker, setShowRefPicker] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(
    {}
  );
  const [selectedRef, setSelectedRef] = useState<RefTarget | null>(null);
  const [labelInput, setLabelInput] = useState("");
  const [lastCursor, setLastCursor] = useState(0);

  const { tree, targetLabelMap } = useMemo(() => {
    let figureCounter = 0;
    let tableCounter = 0;
    const labelMap: Record<string, string> = {};

    const sections: RefTreeSection[] = manual.sections.map((section) => {
      labelMap[section.id] = section.title;

      const sectionRefs: RefTarget[] = [];
      for (const block of section.blocks || []) {
        if (block.layout === "figure") {
          figureCounter += 1;
          const label = `Figure ${figureCounter}`;
          sectionRefs.push({ targetId: block.id, label, kind: "figure" });
          labelMap[block.id] = label;
          continue;
        }

        if (block.layout === "table") {
          tableCounter += 1;
          const label = `Table ${tableCounter}`;
          sectionRefs.push({ targetId: block.id, label, kind: "table" });
          labelMap[block.id] = label;
        }
      }

      const subSections: RefTreeSubSection[] = (section.subSections || []).map(
        (sub) => {
          labelMap[sub.id] = sub.title;

          const subRefs: RefTarget[] = [];
          for (const block of sub.blocks || []) {
            if (block.layout === "figure") {
              figureCounter += 1;
              const label = `Figure ${figureCounter}`;
              subRefs.push({ targetId: block.id, label, kind: "figure" });
              labelMap[block.id] = label;
              continue;
            }

            if (block.layout === "table") {
              tableCounter += 1;
              const label = `Table ${tableCounter}`;
              subRefs.push({ targetId: block.id, label, kind: "table" });
              labelMap[block.id] = label;
            }
          }

          return {
            id: sub.id,
            title: sub.title,
            refs: subRefs,
          };
        }
      );

      return {
        id: section.id,
        title: section.title,
        refs: sectionRefs,
        subSections,
      };
    });

    return { tree: sections, targetLabelMap: labelMap };
  }, [manual.sections]);

  const resolveRequiredLabel = (targetId: string, candidate?: string): string => {
    const trimmed = candidate?.trim() ?? "";
    if (trimmed) return trimmed;
    return targetLabelMap[targetId] || "Reference";
  };

  const [value, setValue] = useState(() =>
    textParts
      .map((p) =>
        p.type === "text"
          ? p.value
          : `[[REF:${p.targetId}|${resolveRequiredLabel(p.targetId, p.label)}]]`
      )
      .join("")
  );

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  const parseToTextParts = (text: string): TextPart[] => {
    const regex = /\[\[REF:([^\]|]+)(?:\|([^\]]*))?\]\]/g;
    const parts: TextPart[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({
          type: "text",
          value: text.slice(lastIndex, match.index),
        });
      }

      parts.push({
        type: "ref",
        targetId: match[1],
        label: resolveRequiredLabel(match[1], match[2]),
      });

      lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push({
        type: "text",
        value: text.slice(lastIndex),
      });
    }

    return parts;
  };

  const insertRefAtCursor = (targetId: string, label: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const token = `[[REF:${targetId}|${label}]]`;
    const newValue =
      value.slice(0, lastCursor) +
      token +
      value.slice(lastCursor);

    setValue(newValue);

    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = lastCursor + token.length;
      textarea.focus();
    }, 0);
  };

  const handleBlur = () => {
    if (showRefPicker) return;

    const parsed = parseToTextParts(value);
    onChange(parsed);
    onDone();
  };

  const handleClose = () => {
    const parsed = parseToTextParts(value);
    onChange(parsed);
    onDone();
  };

  const handlePickRef = (ref: RefTarget) => {
    setSelectedRef(ref);
    setLabelInput(ref.label);
  };

  const isAutoLabeledRef =
    selectedRef?.kind === "figure" || selectedRef?.kind === "table";
  const finalLabel = isAutoLabeledRef
    ? selectedRef?.label ?? ""
    : labelInput.trim();

  return (
    <div className="niquill-inline-editor">
      <div className="niquill-editor-toolbar">
        <button
          type="button"
          className="niquill-ref-button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setShowRefPicker(true)}
        >
          + Reference
        </button>
        <button
          type="button"
          className="niquill-close-button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleClose}
        >
          Close
        </button>
      </div>

      <textarea
        ref={textareaRef}
        className="niquill-inline-textarea"
        value={value}
        spellCheck={true}
        autoCorrect="on"
        autoCapitalize="sentences"
        onChange={(e) => setValue(e.target.value)}
        onClick={(e) =>
          setLastCursor((e.target as HTMLTextAreaElement).selectionStart)
        }
        onKeyUp={(e) =>
          setLastCursor((e.target as HTMLTextAreaElement).selectionStart)
        }
        onBlur={handleBlur}
      />

      {showRefPicker && (
        <div className="niquill-ref-backdrop">
          <div className="niquill-ref-modal" ref={modalRef}>
            <div className="niquill-ref-header">
              <div className="niquill-ref-title">Insert Reference</div>
              <button
                type="button"
                className="niquill-ref-close"
                onClick={() => setShowRefPicker(false)}
              >
                ✕
              </button>
            </div>

            <div className="niquill-ref-body">
              {tree.map((section) => {
                const isOpen = !!expandedSections[section.id];

                return (
                  <div key={section.id} className="niquill-ref-section">
                    <button
                      type="button"
                      className="niquill-ref-toggle"
                      onClick={() =>
                        setExpandedSections((prev) => ({
                          ...prev,
                          [section.id]: !prev[section.id],
                        }))
                      }
                    >
                      <span className="niquill-ref-caret">{isOpen ? "▾" : "▸"}</span>
                      <span>{section.title}</span>
                    </button>

                    {isOpen && (
                      <div className="niquill-ref-content">
                        <button
                          type="button"
                          className={`niquill-ref-item ${
                            selectedRef?.targetId === section.id ? "is-selected" : ""
                          }`}
                          onClick={() =>
                            handlePickRef({
                              targetId: section.id,
                              label: section.title,
                              kind: "section",
                            })
                          }
                        >
                          {section.title}
                        </button>

                        {section.refs.map((ref) => (
                          <button
                            key={ref.targetId}
                            type="button"
                            className={`niquill-ref-item niquill-ref-item--sub ${
                              selectedRef?.targetId === ref.targetId
                                ? "is-selected"
                                : ""
                            }`}
                            onClick={() => handlePickRef(ref)}
                          >
                            {ref.label}
                          </button>
                        ))}

                        {section.subSections.map((sub) => (
                          <div key={sub.id}>
                            <button
                              type="button"
                              className={`niquill-ref-item niquill-ref-item--sub ${
                                selectedRef?.targetId === sub.id ? "is-selected" : ""
                              }`}
                              onClick={() =>
                                handlePickRef({
                                  targetId: sub.id,
                                  label: sub.title,
                                  kind: "subsection",
                                })
                              }
                            >
                              {sub.title}
                            </button>

                            {sub.refs.map((ref) => (
                              <button
                                key={ref.targetId}
                                type="button"
                                className={`niquill-ref-item niquill-ref-item--sub ${
                                  selectedRef?.targetId === ref.targetId
                                    ? "is-selected"
                                    : ""
                                }`}
                                onClick={() => handlePickRef(ref)}
                              >
                                {ref.label}
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="niquill-ref-actions">
              <input
                type="text"
                value={labelInput}
                onChange={(e) => setLabelInput(e.target.value)}
                className="niquill-ref-label-field"
                placeholder="Reference label"
                disabled={!selectedRef || isAutoLabeledRef}
              />
              <button
                className="niquill-ref-add"
                disabled={!selectedRef || !finalLabel}
                onClick={() => {
                  if (!selectedRef || !finalLabel) return;
                  insertRefAtCursor(selectedRef.targetId, finalLabel);
                  setShowRefPicker(false);
                }}
              >
                Add Reference
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
